import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import InviteLeadsForm, {
  InviteLeadsFormValues,
} from 'components/InviteLeadsFromPreviousDeals/components/InviteLeadsForm/InviteLeadsForm.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { LeadStagesCopies } from 'models/DealRoom';
import {
  getArrayLength,
  getCheckedValuesInObject,
  getIsArrayEmpty,
} from 'utils';
import api from 'api';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  LISTING_SHARE_MODE,
  ListingShareInitiatedEventProps,
} from 'models/MixpanelEvents';

import './ShareListingWithPreviousLeads.styles.scss';

type ShareListingWithPreviousLeadsProps = {
  className?: string;
  dealRoomId: string;
  dealRoomName?: string;
  leadStagesCopies?: LeadStagesCopies;
  listingId: string;
  listingName: string;
} & ModalProps;

const ShareListingWithPreviousLeads: React.ForwardRefRenderFunction<
  ModalRef,
  ShareListingWithPreviousLeadsProps
> = (props, ref) => {
  const {
    className,
    dealRoomId,
    dealRoomName,
    leadStagesCopies,
    listingId,
    listingName,
    ...rest
  } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const { popSuccess } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('share-listing-with-previous-leads', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const onClose = useCallback(() => modal?.close(), [modal]);

  const onSubmit = useCallback(
    async (values: InviteLeadsFormValues) => {
      const { leads, includeMessage, customMessage } = values;

      const buyerIds = getCheckedValuesInObject(leads);

      if (getIsArrayEmpty(buyerIds)) return;

      mixpanelService.listingShareInitiated(
        getMixpanelProps({
          listingId,
          listingName,
          listingShareMode: LISTING_SHARE_MODE.PREVIOUS_LEADS,
        }) as ListingShareInitiatedEventProps,
      );

      await api.portfolio.seller.shareListingWithPreviousLead(listingId, {
        buyerIds,
        ...(includeMessage && customMessage && { customMessage }),
      });

      popSuccess(
        t('listingSharedWithLeads', { count: getArrayLength(buyerIds) }),
      );
      onClose();
    },
    [listingId, listingName, getMixpanelProps, onClose, popSuccess, t],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('shareListingWithPreviousLeads')}
      {...rest}
    >
      <InviteLeadsForm
        description={t('shareListingWithPreviousLeadsDescription', {
          dealRoomName: dealRoomName || listingName || t('thisDealRoom'),
        })}
        submitText={t('shareListing')}
        dealRoomId={dealRoomId}
        leadStagesCopies={leadStagesCopies}
        initialValues={{ includeMessage: true, customMessage: '' }}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Modal>
  );
};

export default forwardRef(ShareListingWithPreviousLeads);
